import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BoardColumn, BoardColumnSource, WriteContact, leadStatusSource, pipelineSource } from './boardColumnSource'
import { BoardLane, NOT_STARTED, laneColumns, leadStatusLanes, pipelineLanes } from './boardLanes'
import { UPDATE_CONTACT_MUTATION } from './graphql'
import { readLocationIds } from './locationParam'
import { contactNameTerms } from 'lib/contactSearch'
import { activeSnoozes, loadSnoozes, snoozeContact, useSnoozeVersion } from './momentumStore'
import { ActionQueueDef, QueueSearchOpts } from './queueRegistry'
import type { StageOutcome } from 'app/types/list-board'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import type { ActionCenterUpdateContactMutation } from 'app/generated/gql/graphql'
import { notifyError } from 'app/services/notify.client'
import { ContactJson } from 'app/types/entities'
import { bg } from 'lib/background'
import { debug, logError } from 'lib/logging'

/**
 * The board's data layer: what it loads, how it groups it, and how a move is
 * recorded.
 *
 * A board is not a paged list. Active Leads holds 2,376 contacts; fetching
 * the queue's first page and grouping it client-side would show most of a
 * page in whichever column happens to be biggest and starve the rest, and a
 * global "load more" would not fix that. So a board loads per column instead,
 * in one of two shapes depending on `def.board.source`:
 *
 * - Lead status: one query per column. Each column appends its own
 *   `leadStatus.keyword` term to the queue's search, so it is independently
 *   sorted, independently paged, and takes its true total from its own query.
 * - Pipeline: one query for the pipeline's members, rare enough (73 of
 *   52,193 contacts at Hamblin) to arrive complete in one request, grouped
 *   into stage lanes in memory, plus the queue's own ordinary paged query
 *   supplying the one large column, Not started. See `boardLanes.ts` for the
 *   grouping and count arithmetic, which is pure and tested on its own.
 */

const PAGE_SIZE = 25

/**
 * Upper bound on a pipeline's membership fetch. The whole design rests on
 * membership being rare enough that one request returns everyone; if a
 * pipeline ever grew past this, the board would quietly treat a partial
 * fetch as complete; `debug` below flags that if it ever happens instead of
 * failing silently.
 */
const MEMBER_FETCH_LIMIT = 500

type SortSpec = { field: string; direction: `ASC` | `DESC` }

export type UseContactBoardReturn = {
  lanes: BoardLane[]
  loading: boolean
  error?: string
  /**
   * `write`, when given, replaces the column source's own persistence for
   * this one move. The optimistic relocation and failure rollback still run
   * exactly as they do for every other move. The Closed Lost gate uses this:
   * it needs the milestone mutation to run instead of the plain `leadStatus`
   * write, not in addition to it.
   *
   * Resolves with what became of the move. A caller cannot infer that from
   * the promise alone: a failed move is caught here, rolled back and
   * reported (unless `quiet`, for a bulk run that reports every card once),
   * so it resolves exactly like a successful one. `known` is the contact as
   * the caller holds it, in case the board has let go of it since.
   */
  move: (contactId: string, columnKey: string, opts?: BoardMoveOpts) => Promise<StageOutcome>
  loadMore: (columnKey: string) => void
  /**
   * Which columns have a page request in flight right now. `loading` only
   * ever covers the board's initial read; a load-more happens after that, so
   * a column's own "fetching another page" state has to live separately or a
   * caller has nothing honest to disable a Load more button on.
   */
  loadingColumns: ReadonlySet<string>
  exits: readonly BoardColumn[]
  /**
   * How many moves have landed this session.
   *
   * The exits read their own counts through urql, whose document cache keys
   * on the query and its variables; neither moves when a card lands on an
   * exit, so the count would sit at its page-load value all session. Watching
   * this is what tells them to look again.
   */
  writes: number
  /**
   * Re-reads every column from scratch.
   *
   * A board is a picture of where people are, and a card can be worked
   * somewhere other than the board: the one-at-a-time overlay a card opens
   * moves stages, snoozes and closes leads, and none of that reaches the
   * lists behind it. Without this, closing that overlay leaves a card sitting
   * in the column it has just left.
   */
  refresh: () => void
  /**
   * Put a contact down for a number of days, and take the card off the board.
   *
   * Writes the same per-user, per-queue store the focus flow writes, so the
   * two surfaces of one queue never disagree about who is set aside.
   */
  snooze: (contactId: string, days: number) => void
}

type LaneBranch = {
  lanes: BoardLane[]
  loading: boolean
  error?: string
  loadMore: (columnKey: string) => void
  loadingColumns: ReadonlySet<string>
  /** A failed move has already said so itself, unless the caller asked for `quiet`. */
  move: (contact: ContactJson, columnKey: string, opts?: BranchMoveOpts) => Promise<StageOutcome>
  findContact: (contactId: string) => ContactJson | undefined
}

/** `write` replaces the column source's own persistence; `quiet` leaves a failure unannounced. */
type BranchMoveOpts = { write?: () => Promise<void>; quiet?: boolean }

type BoardMoveOpts = BranchMoveOpts & { known?: ContactJson }

const NOT_MOVED: StageOutcome = { ok: false }

const EMPTY_LOADING_COLUMNS: ReadonlySet<string> = new Set()

const INACTIVE_BRANCH: LaneBranch = {
  lanes: [],
  loading: false,
  loadMore: () => {},
  loadingColumns: EMPTY_LOADING_COLUMNS,
  move: async () => NOT_MOVED,
  findContact: () => undefined,
}

type ColumnFetchState = { contacts: ContactJson[]; total: number; hasMore: boolean; wanted: number }

/**
 * One query per lead-status column. Each column is its own small paged list,
 * so a move just relocates the contact between two of those lists. There is
 * no grouping step the way the pipeline branch needs.
 */
function useLeadStatusBranch(opts: {
  enabled: boolean
  columns: readonly BoardColumn[]
  source: BoardColumnSource | undefined
  baseSearch: string | undefined
  sort: SortSpec | undefined
  snoozedIds: Set<string>
  /** Bumped to re-read the board. See `UseContactBoardReturn.refresh`. */
  reloadNonce: number
}): LaneBranch {
  const [state, setState] = useState<Record<string, ColumnFetchState>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [loadingColumns, setLoadingColumns] = useState<Set<string>>(new Set())
  const generation = useRef(0)
  const lanesColumns = useMemo(() => laneColumns(opts.columns), [opts.columns])
  const columnKeysJoined = lanesColumns.map((c) => c.key).join(`|`)

  const stopLoadingColumn = useCallback((columnKey: string) => {
    setLoadingColumns((prev) => {
      if (!prev.has(columnKey)) return prev
      const next = new Set(prev)
      next.delete(columnKey)
      return next
    })
  }, [])

  const fetchColumn = useCallback(
    (column: BoardColumn, page: number, gen: number, search: string, sort: SortSpec) =>
      gqlFetchList(`contacts`, { search, pagination: { page, limit: PAGE_SIZE }, sort })
        .then(({ hits, total }) => {
          if (gen !== generation.current) return
          const fresh = hits.filter((c) => c.id && !opts.snoozedIds.has(c.id))
          setState((prev) => {
            const existing = prev[column.key]
            const contacts = page === 0 ? fresh : [...(existing?.contacts ?? []), ...fresh]
            return {
              ...prev,
              [column.key]: {
                contacts,
                total: Math.max(0, total - opts.snoozedIds.size),
                hasMore: (page + 1) * PAGE_SIZE < total,
                wanted: page,
              },
            }
          })
        })
        .catch((err) => {
          if (gen !== generation.current) return
          logError(`Error loading Action Center board column`, column.key, err)
          setError(`Couldn't load the board. Please try again.`)
        })
        // Runs on both the happy path and the catch above, so a failed page
        // request still frees the column's Load more control instead of
        // leaving it disabled for the rest of the session.
        .finally(() => {
          if (gen === generation.current) stopLoadingColumn(column.key)
        }),
    [opts.snoozedIds, stopLoadingColumn],
  )

  // What makes this a different board rather than the same one read again.
  // A refresh (see `UseContactBoardReturn.refresh`) bumps only the nonce.
  const identity = `${opts.enabled}|${opts.baseSearch}|${opts.sort?.field}|${opts.sort?.direction}|${columnKeysJoined}`
  const identityRef = useRef(identity)

  useEffect(() => {
    if (!opts.enabled || !opts.baseSearch || !opts.sort || lanesColumns.length === 0) return
    const gen = ++generation.current
    const baseSearch = opts.baseSearch
    const sort = opts.sort
    // Emptying the lanes is for a board that is about to show different
    // people. A re-read of the same board keeps its cards on screen until the
    // new ones arrive: closing the drawer used to blank every column for a
    // second, which reads as the board breaking rather than catching up.
    const isNewBoard = identityRef.current !== identity
    identityRef.current = identity
    if (isNewBoard) {
      setState({})
      setLoading(true)
    }
    setError(undefined)
    setLoadingColumns(new Set())
    bg(
      Promise.all(
        lanesColumns.map((column) =>
          fetchColumn(column, 0, gen, `${baseSearch} leadStatus.keyword:"${column.key}"`, sort),
        ),
      ).finally(() => {
        if (gen === generation.current) setLoading(false)
      }),
    )
    // columnKeysJoined stands in for lanesColumns/fetchColumn: both are rebuilt
    // every render, and depending on their references directly would re-run
    // this load every render rather than only when the columns actually change.
  }, [opts.enabled, opts.baseSearch, opts.sort?.field, opts.sort?.direction, columnKeysJoined, opts.reloadNonce])

  const loadMore = useCallback(
    (columnKey: string) => {
      const column = lanesColumns.find((c) => c.key === columnKey)
      const current = state[columnKey]
      if (!column || !current?.hasMore || !opts.baseSearch || !opts.sort) return
      setLoadingColumns((prev) => new Set(prev).add(columnKey))
      bg(
        fetchColumn(
          column,
          current.wanted + 1,
          generation.current,
          `${opts.baseSearch} leadStatus.keyword:"${column.key}"`,
          opts.sort,
        ),
      )
    },
    [lanesColumns, state, fetchColumn, opts.baseSearch, opts.sort],
  )

  const findContact = useCallback(
    (contactId: string) => {
      for (const column of lanesColumns) {
        const found = state[column.key]?.contacts.find((c) => c.id === contactId)
        if (found) return found
      }
      return undefined
    },
    [lanesColumns, state],
  )

  const move = useCallback(
    async (contact: ContactJson, columnKey: string, { write, quiet }: BranchMoveOpts = {}): Promise<StageOutcome> => {
      if (!opts.source || !contact.id) return NOT_MOVED
      const fromColumn = opts.source.columnOf(contact)
      const optimisticContact = opts.source.applyMove(contact, columnKey)
      setState((prev) => {
        const next = { ...prev }
        if (fromColumn && next[fromColumn]) {
          next[fromColumn] = {
            ...next[fromColumn],
            contacts: next[fromColumn].contacts.filter((c) => c.id !== contact.id),
            total: Math.max(0, next[fromColumn].total - 1),
          }
        }
        // A target that is not a lane (an exit column) has no entry here, so
        // this step no-ops and the card simply leaves the board, which is
        // correct: the queue's own search stops matching it either way.
        if (next[columnKey]) {
          next[columnKey] = {
            ...next[columnKey],
            contacts: [optimisticContact, ...next[columnKey].contacts],
            total: next[columnKey].total + 1,
          }
        }
        return next
      })
      try {
        // `write` overrides the column source's own persistence rather than
        // running alongside it. See the doc comment on `UseContactBoardReturn.move`.
        await (write ? write() : opts.source.move(contact, columnKey))
        return { ok: true }
      } catch (err) {
        logError(`Error moving Action Center board card`, err)
        if (!quiet) notifyError(`Couldn't save that move. It's back where it was.`)
        setState((prev) => {
          const next = { ...prev }
          if (next[columnKey]) {
            next[columnKey] = {
              ...next[columnKey],
              contacts: next[columnKey].contacts.filter((c) => c.id !== contact.id),
              total: Math.max(0, next[columnKey].total - 1),
            }
          }
          if (fromColumn && next[fromColumn]) {
            next[fromColumn] = {
              ...next[fromColumn],
              contacts: [contact, ...next[fromColumn].contacts],
              total: next[fromColumn].total + 1,
            }
          }
          return next
        })
        return { ok: false, reason: err instanceof Error ? err.message : `Couldn't save that move` }
      }
    },
    [opts.source],
  )

  const perColumn = useMemo(() => new Map(Object.entries(state)), [state])
  const lanes = useMemo(() => leadStatusLanes(opts.columns, perColumn), [opts.columns, perColumn])

  return { lanes, loading, error, loadMore, loadingColumns, move, findContact }
}

type NotStartedFetchState = { contacts: ContactJson[]; hasMore: boolean; wanted: number }
const EMPTY_NOT_STARTED: NotStartedFetchState = { contacts: [], hasMore: false, wanted: 0 }

/**
 * One membership fetch grouped into stage lanes, plus the queue's own ordinary
 * paged query for Not started. See `boardLanes.ts` (`pipelineLanes`) for the
 * grouping itself; this hook only owns the fetching and the optimistic write.
 */
function usePipelineBranch(opts: {
  enabled: boolean
  pipelineId: string | undefined
  columns: readonly BoardColumn[]
  source: BoardColumnSource | undefined
  writeContact: WriteContact
  baseSearch: string | undefined
  sort: SortSpec | undefined
  snoozedIds: Set<string>
  /** Bumped to re-read the board. See `UseContactBoardReturn.refresh`. */
  reloadNonce: number
}): LaneBranch {
  const [members, setMembers] = useState<ContactJson[]>([])
  const [queueTotal, setQueueTotal] = useState(0)
  const [notStarted, setNotStarted] = useState<NotStartedFetchState>(EMPTY_NOT_STARTED)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  // Only Not started ever pages (see `loadMore` below), so this is at most a
  // one-element set, but it stays a set to match `LaneBranch`'s shape and
  // keep both branches interchangeable to their caller.
  const [loadingColumns, setLoadingColumns] = useState<Set<string>>(new Set())
  const generation = useRef(0)
  // See the lead-status branch: only a genuinely different board empties.
  const identity = `${opts.enabled}|${opts.pipelineId}|${opts.baseSearch}|${opts.sort?.field}|${opts.sort?.direction}`
  const identityRef = useRef(identity)

  useEffect(() => {
    if (!opts.enabled || !opts.pipelineId || !opts.baseSearch || !opts.sort) return
    const gen = ++generation.current
    const pipelineId = opts.pipelineId
    const baseSearch = opts.baseSearch
    const sort = opts.sort
    const isNewBoard = identityRef.current !== identity
    identityRef.current = identity
    if (isNewBoard) {
      setMembers([])
      setQueueTotal(0)
      setNotStarted(EMPTY_NOT_STARTED)
      setLoading(true)
    }
    setError(undefined)
    setLoadingColumns(new Set())
    bg(
      Promise.all([
        // Reserved ids carry a colon (`system:hygiene`), which Lucene reads as
        // a field separator, so the id is quoted rather than bare.
        gqlFetchList(`contacts`, {
          search: `${baseSearch} pipelines.id:"${pipelineId}"`,
          pagination: { limit: MEMBER_FETCH_LIMIT },
          sort,
        }),
        gqlFetchList(`contacts`, { search: baseSearch, pagination: { page: 0, limit: PAGE_SIZE }, sort }),
      ])
        .then(([memberResult, notStartedResult]) => {
          if (gen !== generation.current) return
          if (memberResult.total > memberResult.hits.length) {
            // The design assumes membership fits one request; if that stopped
            // being true the board would silently show an incomplete set of
            // stage columns instead of failing loudly, so this says so.
            debug(`action-center`, `pipeline board: membership exceeds the fetch limit`, {
              pipelineId,
              total: memberResult.total,
              fetched: memberResult.hits.length,
            })
          }
          setMembers(memberResult.hits.filter((c) => c.id && !opts.snoozedIds.has(c.id)))
          setQueueTotal(Math.max(0, notStartedResult.total - opts.snoozedIds.size))
          const firstPage = notStartedResult.hits.filter((c) => c.id && !opts.snoozedIds.has(c.id))
          setNotStarted({
            contacts: firstPage,
            hasMore: PAGE_SIZE < notStartedResult.total,
            wanted: 0,
          })
        })
        .catch((err) => {
          if (gen !== generation.current) return
          logError(`Error loading Action Center pipeline board`, err)
          setError(`Couldn't load the board. Please try again.`)
        })
        .finally(() => {
          if (gen === generation.current) setLoading(false)
        }),
    )
  }, [
    opts.enabled,
    opts.pipelineId,
    opts.baseSearch,
    opts.sort?.field,
    opts.sort?.direction,
    opts.snoozedIds,
    opts.reloadNonce,
  ])

  const loadMore = useCallback(
    (columnKey: string) => {
      // Stage lanes came from the one complete members fetch; there is
      // nothing to page there. Only Not started ever has more.
      if (columnKey !== NOT_STARTED.key || !notStarted.hasMore || !opts.baseSearch || !opts.sort) return
      const gen = generation.current
      const nextPage = notStarted.wanted + 1
      const baseSearch = opts.baseSearch
      const sort = opts.sort
      setLoadingColumns((prev) => new Set(prev).add(NOT_STARTED.key))
      bg(
        gqlFetchList(`contacts`, {
          search: baseSearch,
          pagination: { page: nextPage, limit: PAGE_SIZE },
          sort,
        })
          .then(({ hits, total }) => {
            if (gen !== generation.current) return
            const fresh = hits.filter((c) => c.id && !opts.snoozedIds.has(c.id))
            setNotStarted((prev) => ({
              contacts: [...prev.contacts, ...fresh],
              hasMore: (nextPage + 1) * PAGE_SIZE < total,
              wanted: nextPage,
            }))
            setQueueTotal(Math.max(0, total - opts.snoozedIds.size))
          })
          .catch((err) => {
            logError(`Error loading more of the Action Center board`, err)
            notifyError(`Couldn't load more. Please try again.`)
          })
          // On the happy path and the failure alike: a failed page request
          // must still free the button, or it reads "Loading more" forever.
          .finally(() => {
            setLoadingColumns((prev) => {
              if (!prev.has(NOT_STARTED.key)) return prev
              const next = new Set(prev)
              next.delete(NOT_STARTED.key)
              return next
            })
          }),
      )
    },
    [notStarted.hasMore, notStarted.wanted, opts.baseSearch, opts.sort, opts.snoozedIds],
  )

  const findContact = useCallback(
    (contactId: string) =>
      members.find((c) => c.id === contactId) ?? notStarted.contacts.find((c) => c.id === contactId),
    [members, notStarted.contacts],
  )

  const move = useCallback(
    async (contact: ContactJson, columnKey: string, { quiet }: BranchMoveOpts = {}): Promise<StageOutcome> => {
      if (!opts.source || !contact.id || !opts.pipelineId) return NOT_MOVED
      const wasMember = members.some((c) => c.id === contact.id)

      // Every column is a real stage now, Not started included, so a move is
      // always a stage write: nothing here removes a membership.
      //
      // A real stage, exit or not (see boardColumnSource.ts: `exit` still
      // rests on a real stage name. It just leaves the board's rendered
      // lanes because the queue stops matching it, not because the pipeline
      // membership itself goes away). Either way the contact stays a member.
      const optimisticContact = opts.source.applyMove(contact, columnKey)
      setMembers((prev) => [...prev.filter((c) => c.id !== contact.id), optimisticContact])
      if (!wasMember) setNotStarted((prev) => ({ ...prev, contacts: prev.contacts.filter((c) => c.id !== contact.id) }))
      try {
        await opts.source.move(contact, columnKey)
        return { ok: true }
      } catch (err) {
        logError(`Error moving Action Center board card`, err)
        if (!quiet) notifyError(`Couldn't save that move. It's back where it was.`)
        if (wasMember) {
          setMembers((prev) => [...prev.filter((c) => c.id !== contact.id), contact])
        } else {
          setMembers((prev) => prev.filter((c) => c.id !== contact.id))
          setNotStarted((prev) => ({ ...prev, contacts: [contact, ...prev.contacts] }))
        }
        return { ok: false, reason: err instanceof Error ? err.message : `Couldn't save that move` }
      }
    },
    [opts.source, opts.pipelineId, members],
  )

  const lanes = useMemo(() => {
    if (!opts.source) return []
    return pipelineLanes({
      columns: opts.columns,
      columnOf: opts.source.columnOf,
      members,
      queueTotal,
      notStartedPage: notStarted.contacts,
      notStartedHasMore: notStarted.hasMore,
    })
  }, [opts.source, opts.columns, members, queueTotal, notStarted])

  return { lanes, loading, error, loadMore, loadingColumns, move, findContact }
}

/**
 * A queue's board: what loads, how it groups, and how a move is recorded.
 * Returns empty/inert data for a queue with no `board` configured.
 */
export function useContactBoard(
  def: ActionQueueDef | undefined,
  /**
   * Who is looking, and where.
   *
   * The workspace is what a snooze is filed under, because a queue is shared
   * work: see the note at the top of `momentumStore`. The user comes along to
   * be recorded on one, not to key it.
   */
  viewer: { userId?: string; workspaceId?: string },
  /**
   * Replaces `def.searchTerms` for this one read, same override shape as
   * `move`'s own `write` param below. The Hygiene Recall board uses this to
   * narrow to one of the three hygiene windows (or their union for "All")
   * without needing a second `ActionQueueDef` per band: everything else about
   * the board (its columns, its sort, its pipeline) still comes from `def`.
   */
  searchTermsOverride?: (opts: QueueSearchOpts) => string[],
  /**
   * Replaces `def.sort` for this one read, the same override shape and the
   * same reason as `searchTermsOverride`: a band is not just a narrower
   * search, it is often a different order. Soonest first while a cleaning is
   * still ahead; most recently due first once it has passed.
   */
  sortOverride?: (opts: QueueSearchOpts) => SortSpec | undefined,
): UseContactBoardReturn {
  const needsActionDateEnabled = !!useFeatureToggle(`needs-action-date`)
  const [searchParams] = useSearchParams()
  const filtersParam = searchParams.get(`filters`)
  const locationIds = useMemo(() => readLocationIds(filtersParam), [filtersParam])
  // What was typed in the header's search box. The shared `<Search />` writes
  // it to the URL, the same param the contacts and pipelines pages read.
  const query = searchParams.get(`query`) ?? ``

  const writeContact = useCallback<WriteContact>(async (id, input) => {
    const result = await gqlMutation(UPDATE_CONTACT_MUTATION, { id, input })
    if (result.error) throw new Error(result.error.message)
  }, [])

  const source = useMemo<BoardColumnSource | undefined>(() => {
    if (!def?.board) return undefined
    return def.board.source === `lead-status`
      ? leadStatusSource(writeContact)
      : pipelineSource(def.board.pipelineId, writeContact)
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
  }, [def?.board, writeContact])

  const columns = useMemo(() => source?.columns() ?? [], [source])
  const exits = useMemo(() => columns.filter((c) => c.exit), [columns])

  // Same search the focus flow builds: the queue's own terms plus the
  // location drill-down carried over from the overview.
  const baseSearch = useMemo(() => {
    if (!def) return undefined
    const terms = (searchTermsOverride ?? def.searchTerms)({ needsActionDateEnabled })
    if (locationIds.length) terms.push(`locationId:(${locationIds.map((id) => `"${id}"`).join(` OR `)})`)
    // Searched at the index rather than filtered in the browser. A lane holds
    // 29 of 432 people until somebody pages through it, so a filter over what
    // happens to be loaded would answer "no matches" for somebody who is
    // plainly in the queue. `contactNameTerms` escapes what was typed: this
    // string is handed to ES `query_string` as written, where a stray colon
    // starts a field clause and a bare `!` becomes NOT.
    terms.push(...contactNameTerms(query))
    return terms.join(` `)
  }, [def, searchTermsOverride, needsActionDateEnabled, locationIds, query])

  const sort = useMemo<SortSpec | undefined>(
    () => sortOverride?.({ needsActionDateEnabled }) ?? def?.sort({ needsActionDateEnabled }),
    [def, sortOverride, needsActionDateEnabled],
  )

  // Snoozes are per-queue and per-user, the same scoping useFocusQueue
  // applies: a lead snoozed on Active Leads should not shrink or hide from
  // another queue's board.
  const boardKind = def?.board?.source
  const pipelineId = def?.board?.source === `pipeline` ? def.board.pipelineId : undefined

  // One counter for both branches; only the enabled one is reading.
  const [reloadNonce, setReloadNonce] = useState(0)
  const refresh = useCallback(() => setReloadNonce((previous) => previous + 1), [])
  /** How many moves have landed this session. See `move` below. */
  const [writes, setWrites] = useState(0)

  // The snoozes live on the API now, fetched once per load into the store's
  // cache. The version subscription is what re-runs this memo when that fetch
  // lands, or when any surface snoozes somebody, so a card set aside in the
  // focus flow is gone from the board without a reload.
  const snoozeVersion = useSnoozeVersion()
  useEffect(() => {
    if (viewer.workspaceId) bg(loadSnoozes(viewer.workspaceId))
  }, [viewer.workspaceId])
  const snoozedIds = useMemo(() => {
    if (!def || !viewer.workspaceId) return new Set<string>()
    return new Set(
      activeSnoozes(viewer.workspaceId)
        .filter((s) => s.queueId === def.id)
        .map((s) => s.contactId),
    )
  }, [def, viewer.workspaceId, reloadNonce, snoozeVersion])

  const leadStatusBranch = useLeadStatusBranch({
    enabled: boardKind === `lead-status`,
    columns,
    source,
    baseSearch,
    sort,
    snoozedIds,
    reloadNonce,
  })
  const pipelineBranch = usePipelineBranch({
    enabled: boardKind === `pipeline`,
    pipelineId,
    columns,
    source,
    writeContact,
    baseSearch,
    sort,
    snoozedIds,
    reloadNonce,
  })

  const active =
    boardKind === `lead-status` ? leadStatusBranch : boardKind === `pipeline` ? pipelineBranch : INACTIVE_BRANCH

  /**
   * Put somebody down for a while, from the board.
   *
   * The same store the focus flow writes, so a contact snoozed in one is gone
   * from the other: `snoozeContact` is keyed by workspace and queue, and both
   * surfaces of a queue read the same list. Bumping the nonce is what makes
   * the card leave now rather than at the next reload, since the snooze list
   * is read from localStorage rather than subscribed to.
   */
  const snooze = useCallback(
    (contactId: string, days: number) => {
      if (!def || !viewer.workspaceId) return
      const until = new Date()
      until.setDate(until.getDate() + days)
      snoozeContact(viewer.workspaceId, contactId, def.id, until, viewer.userId)
      setReloadNonce((previous) => previous + 1)
    },
    [def, viewer.workspaceId, viewer.userId],
  )

  const move = useCallback(
    async (
      contactId: string,
      columnKey: string,
      { write, quiet, known }: BoardMoveOpts = {},
    ): Promise<StageOutcome> => {
      const contact = active.findContact(contactId) ?? (known?.id === contactId ? known : undefined)
      if (!contact) return { ok: false, reason: `This contact is no longer on screen` }
      // Picking a card up and putting it back where it was is not a move.
      // Without this it was treated as one: the record was rewritten with the
      // stage it already held, and the optimistic pass pulled the card out of
      // its lane and pushed it back in at the top, so a gesture that meant
      // nothing reordered the column and spent a write. When that write
      // failed it also raised "Couldn't save that move" over a move nobody
      // made.
      //
      // `columnOf` is undefined for somebody on the board who holds no
      // membership yet, which is deliberately not the same as a match: they
      // sit in the first lane without being in the pipeline, and dropping
      // them there is the write that enrols them.
      if (source?.columnOf(contact) === columnKey) return { ok: false, skipped: true, reason: `Already at this stage` }
      const outcome = await active.move(contact, columnKey, { write, quiet })
      // The exits count their people with a query of their own, and neither
      // that query nor its variables change when a card lands on one, so urql
      // would serve the page-load answer for the rest of the session. This is
      // what tells them to look again.
      if (outcome.ok) setWrites((previous) => previous + 1)
      return outcome
    },
    [active, source],
  )

  const loadMore = useCallback((columnKey: string) => active.loadMore(columnKey), [active])

  return {
    lanes: active.lanes,
    loading: active.loading,
    error: active.error,
    move,
    loadMore,
    loadingColumns: active.loadingColumns,
    exits,
    writes,
    refresh,
    snooze,
  }
}
